import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { View, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity } from "react-native"
import Slider from "@react-native-community/slider"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import BrevetFunnelHeader from "./components/BrevetFunnelHeader"
import BrevetVisualCard from "./components/BrevetVisualCard"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"
import { brevetFunnelService } from "./services/brevetFunnelService"

// Mapping hours slider → bucket back-compat pour recommendFormula
const hoursToTimeBucket = (h) => {
    if (h <= 1.25) return "1h"
    if (h >= 3.75) return "4h_plus"
    return "2_3h"
}

const BREVET_BLANC = [
    { id: "en_difficulte", label: "Note < 8", emoji: "😟" },
    { id: "moyens", label: "Note 8-12", emoji: "😐" },
    { id: "ca_va", label: "Note 12-14", emoji: "🙂" },
    { id: "tres_bien", label: "Note > 14", emoji: "😊" },
]

const AUTONOMIE = [
    { id: "jamais", label: "Jamais", emoji: "😟" },
    { id: "rarement", label: "Rarement", emoji: "😐" },
    { id: "parfois", label: "Parfois", emoji: "🙂" },
    { id: "toujours", label: "Toujours", emoji: "😊" },
]

const BESOINS = [
    { id: "dys", label: "Dys" },
    { id: "tdah", label: "TDAH" },
    { id: "hpi", label: "HPI" },
    { id: "autre", label: "Autre" },
    { id: "aucun", label: "Aucun" },
]

// Pattern inspiré onboarding V2 : catégories communes en chips primaires, sous-chips spécifiques.
const INTERESTS_CATEGORIES = [
    {
        id: "sport",
        label: "Sport",
        emoji: "⚽",
        sub: ["Football", "Basket", "Tennis", "Danse", "Arts martiaux", "Natation"],
    },
    {
        id: "creatif",
        label: "Créatif",
        emoji: "🎨",
        sub: ["Dessin", "Peinture", "DIY", "Photo", "Mode"],
    },
    {
        id: "musique",
        label: "Musique",
        emoji: "🎵",
        sub: ["Piano", "Guitare", "Chant", "Hip-hop", "K-pop", "Rock"],
    },
    {
        id: "numerique",
        label: "Numérique",
        emoji: "🎮",
        sub: ["Jeux vidéo", "Programmation", "Esport", "Réseaux sociaux"],
    },
    {
        id: "lecture",
        label: "Lecture & culture",
        emoji: "📚",
        sub: ["Manga", "BD", "Romans", "Cinéma", "Séries"],
    },
    {
        id: "sciences",
        label: "Sciences",
        emoji: "🔬",
        sub: ["Espace", "Animaux", "Tech", "Histoire"],
    },
]

const InfoLine = ({ label, text, isLast }) => (
    <View style={[infoStyles.row, isLast && { marginBottom: 0 }]}>
        <Text style={infoStyles.label}>{label}</Text>
        <Text style={infoStyles.text}>{text}</Text>
    </View>
)

const BrevetDiagnosticStep2Screen = (props) => {
    const { navigation } = props
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useBrevetFunnel()
    const scrollRef = useRef(null)
    const anchors = useRef({})

    const [brevetBlanc, setBrevetBlanc] = useState(state.brevetBlanc || null)
    const [autonomie, setAutonomie] = useState(state.autonomie || null)
    const [timeHours, setTimeHours] = useState(
        typeof state.timeHours === "number" ? state.timeHours : 2
    )
    const timeBucket = useMemo(() => hoursToTimeBucket(timeHours), [timeHours])
    const [besoinsSpeciaux, setBesoinsSpeciaux] = useState(
        state.besoinsSpeciaux || []
    )
    const [besoinsInfoOpen, setBesoinsInfoOpen] = useState(false)
    const [interetsTags, setInteretsTags] = useState(() => {
        // Restauration depuis state.interets si c'est déjà du JSON tags, sinon vide
        if (Array.isArray(state.interetsTags)) return state.interetsTags
        return []
    })
    const [interetsFreeText, setInteretsFreeText] = useState(
        state.interetsFreeText || ""
    )

    // Mirror local edits to the context so a refresh mid-funnel restores partial answers
    // (sessionStorage on web). Keeps existing onContinue payload untouched.
    useEffect(() => {
        update({
            brevetBlanc,
            autonomie,
            timeHours,
            timePerWeekend: timeBucket,
            besoinsSpeciaux,
            interetsTags,
            interetsFreeText,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        brevetBlanc,
        autonomie,
        timeHours,
        timeBucket,
        besoinsSpeciaux,
        interetsTags,
        interetsFreeText,
    ])
    const [activeCategory, setActiveCategory] = useState(null)
    const [error, setError] = useState(null)

    const scrollToAnchor = (key) => {
        const y = anchors.current[key] || 0
        scrollRef.current?.scrollTo({ y: Math.max(0, y - 20), animated: true })
    }

    const onContinue = () => {
        if (!brevetBlanc) {
            setError("brevetBlanc")
            scrollToAnchor("brevetBlanc")
            return
        }
        if (!autonomie) {
            setError("autonomie")
            scrollToAnchor("autonomie")
            return
        }
        const interetsResume = [
            ...interetsTags,
            ...(interetsFreeText.trim() ? [interetsFreeText.trim()] : []),
        ].join(", ")
        update({
            brevetBlanc,
            autonomie,
            timeHours,
            timePerWeekend: timeBucket,
            besoinsSpeciaux,
            interetsTags,
            interetsFreeText,
            interets: interetsResume,
        })
        if (state.funnelStateId) {
            props.apiRequest(
                brevetFunnelService.updateAnswers,
                { stateId: state.funnelStateId },
                {
                    subjects: state.subjects,
                    selectedModules: state.selectedModules,
                    objective: state.objective,
                    brevetBlanc,
                    autonomie,
                    timeHours,
                    timePerWeekend: timeBucket,
                    besoinsSpeciaux,
                    interets: interetsResume,
                }
            )
        }
        amplitude.track("brevet_diag_completed", {
            funnel_state_id: state.funnelStateId,
            objective: state.objective,
            subjects: state.subjects,
            time_per_weekend: timeBucket,
            referral_code: state.referralCode || null,
        })
        navigation.navigate("brevet/reco")
    }

    const toggleBesoin = (id) => {
        if (id === "aucun") {
            setBesoinsSpeciaux(besoinsSpeciaux.includes("aucun") ? [] : ["aucun"])
            return
        }
        const next = besoinsSpeciaux.includes(id)
            ? besoinsSpeciaux.filter((b) => b !== id)
            : [...besoinsSpeciaux.filter((b) => b !== "aucun"), id]
        setBesoinsSpeciaux(next)
    }

    const toggleInterestTag = (label) => {
        setInteretsTags((prev) =>
            prev.includes(label) ? prev.filter((t) => t !== label) : [...prev, label]
        )
    }

    const firstname = state.studentFirstname || "votre enfant"
    const studentClass = state.studentClass || "3ème"
    const containerWidth = Math.min(dimensions?.width || 400, 460)

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={2}
                    total={4}
                    onBack={() => navigation.goBack()}
                />
                <ScrollView
                    ref={scrollRef}
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    <Text style={styles.title}>Personnalisons le Sprint de {firstname}</Text>
                    <View style={styles.reassureRow}>
                        <FontAwesome5
                            name="lock"
                            size={10}
                            color={theme.colors.light}
                        />
                        <Text style={styles.reassureText}>
                            Vos réponses restent privées et servent uniquement à adapter les cours.
                        </Text>
                    </View>

                    {/* Brevet blanc — 1 ligne 4 cards */}
                    <View
                        onLayout={(e) =>
                            (anchors.current.brevetBlanc = e.nativeEvent.layout.y)
                        }
                        style={styles.block}
                    >
                        <Text style={styles.blockTitle}>Résultats au brevet blanc passé</Text>
                        <View style={styles.rowCards}>
                            {BREVET_BLANC.map((b) => (
                                <View key={b.id} style={styles.rowCardWrap}>
                                    <BrevetVisualCard
                                        emoji={b.emoji}
                                        label={b.label}
                                        selected={brevetBlanc === b.id}
                                        onPress={() => {
                                            setError(null)
                                            setBrevetBlanc(b.id)
                                        }}
                                        compact
                                    />
                                </View>
                            ))}
                        </View>
                        {error === "brevetBlanc" && (
                            <Text style={styles.errorText}>
                                Choisissez le résultat brevet blanc.
                            </Text>
                        )}
                    </View>

                    {/* Autonomie devoirs — pattern smiley comme niveau + brevet blanc */}
                    <View
                        style={styles.block}
                        onLayout={(e) =>
                            (anchors.current.autonomie = e.nativeEvent.layout.y)
                        }
                    >
                        <Text style={styles.blockTitle}>Fait ses devoirs seul ?</Text>
                        <View style={styles.rowCards}>
                            {AUTONOMIE.map((a) => (
                                <View key={a.id} style={styles.rowCardWrap}>
                                    <BrevetVisualCard
                                        emoji={a.emoji}
                                        label={a.label}
                                        selected={autonomie === a.id}
                                        onPress={() => {
                                            setError(null)
                                            setAutonomie(a.id)
                                        }}
                                        compact
                                    />
                                </View>
                            ))}
                        </View>
                        {error === "autonomie" && (
                            <Text style={styles.errorText}>
                                Indiquez l'autonomie de {firstname}.
                            </Text>
                        )}
                    </View>

                    {/* Temps dispo — slider 1h → 6h, défaut 2h */}
                    <View style={styles.block}>
                        <Text style={styles.blockTitle}>Temps dispo par week-end</Text>
                        <View style={styles.sliderRow}>
                            <Slider
                                style={styles.slider}
                                minimumValue={1}
                                maximumValue={6}
                                step={0.5}
                                value={timeHours}
                                onValueChange={setTimeHours}
                                minimumTrackTintColor={theme.colors.primary}
                                maximumTrackTintColor={theme.colors.lightMore}
                                thumbTintColor={theme.colors.primary}
                            />
                        </View>
                        <View style={styles.sliderEnds}>
                            <Text style={styles.sliderEnd}>1 h</Text>
                            <Text style={styles.sliderValue}>
                                {Number.isInteger(timeHours)
                                    ? `${timeHours} h`
                                    : `${timeHours.toFixed(1).replace(".", ",")} h`}
                                {" "}/ week-end
                            </Text>
                            <Text style={styles.sliderEnd}>6 h+</Text>
                        </View>
                    </View>

                    {/* Besoins particuliers */}
                    <View style={styles.block}>
                        <View style={styles.blockTitleRow}>
                            <Text style={styles.blockTitle}>
                                Besoins particuliers
                                <Text style={styles.optional}>{" "}· optionnel</Text>
                            </Text>
                            <TouchableOpacity
                                onPress={() => setBesoinsInfoOpen((v) => !v)}
                                activeOpacity={0.7}
                                style={styles.infoButton}
                                accessibilityLabel="Définitions des besoins particuliers"
                            >
                                <FontAwesome5
                                    name="info-circle"
                                    size={14}
                                    color={
                                        besoinsInfoOpen
                                            ? theme.colors.primary
                                            : theme.colors.light
                                    }
                                />
                            </TouchableOpacity>
                        </View>

                        {besoinsInfoOpen ? (
                            <View style={styles.infoBox}>
                                <InfoLine
                                    label="Dys"
                                    text="Dyslexie, dyspraxie, dyscalculie, dysorthographie — troubles spécifiques de l'apprentissage."
                                />
                                <InfoLine
                                    label="TDAH"
                                    text="Trouble du déficit de l'attention avec ou sans hyperactivité."
                                />
                                <InfoLine
                                    label="HPI"
                                    text="Haut potentiel intellectuel — besoin d'un rythme plus exigeant."
                                />
                                <InfoLine
                                    label="Autre"
                                    text="À préciser au tuteur lors du premier cours."
                                    isLast
                                />
                            </View>
                        ) : null}

                        <View style={styles.pillsRow}>
                            {BESOINS.map((b) => (
                                <TouchableOpacity
                                    key={b.id}
                                    onPress={() => toggleBesoin(b.id)}
                                    activeOpacity={0.7}
                                    style={[
                                        styles.pillSmall,
                                        besoinsSpeciaux.includes(b.id) &&
                                            styles.pillSmallActive,
                                    ]}
                                >
                                    <Text
                                        style={[
                                            styles.pillSmallLabel,
                                            besoinsSpeciaux.includes(b.id) &&
                                                styles.pillSmallLabelActive,
                                        ]}
                                    >
                                        {b.label}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </View>

                    {/* Centres d'intérêt — catégories + sub-select chips (inspi onboarding V2) */}
                    <View style={styles.block}>
                        <Text style={styles.blockTitle}>
                            Centres d'intérêt
                            <Text style={styles.optional}>{" "}· optionnel</Text>
                        </Text>
                        <Text style={styles.blockHint}>
                            Pour rendre les exercices plus attrayants pour {firstname}.
                        </Text>
                        <View style={styles.pillsRow}>
                            {INTERESTS_CATEGORIES.map((cat) => {
                                const open = activeCategory === cat.id
                                const hasPicked = cat.sub.some((s) =>
                                    interetsTags.includes(s)
                                )
                                return (
                                    <TouchableOpacity
                                        key={cat.id}
                                        onPress={() =>
                                            setActiveCategory(open ? null : cat.id)
                                        }
                                        activeOpacity={0.7}
                                        style={[
                                            styles.categoryPill,
                                            (open || hasPicked) && styles.categoryPillActive,
                                        ]}
                                    >
                                        <Text style={styles.categoryEmoji}>
                                            {cat.emoji}
                                        </Text>
                                        <Text
                                            style={[
                                                styles.categoryLabel,
                                                (open || hasPicked) &&
                                                    styles.categoryLabelActive,
                                            ]}
                                        >
                                            {cat.label}
                                        </Text>
                                        {hasPicked ? (
                                            <View style={styles.categoryBadge}>
                                                <Text style={styles.categoryBadgeText}>
                                                    {
                                                        cat.sub.filter((s) =>
                                                            interetsTags.includes(s)
                                                        ).length
                                                    }
                                                </Text>
                                            </View>
                                        ) : null}
                                    </TouchableOpacity>
                                )
                            })}
                        </View>

                        {activeCategory ? (
                            <View style={styles.subChipsBox}>
                                <View style={styles.pillsRow}>
                                    {INTERESTS_CATEGORIES.find(
                                        (c) => c.id === activeCategory
                                    )?.sub.map((label) => (
                                        <TouchableOpacity
                                            key={label}
                                            onPress={() => toggleInterestTag(label)}
                                            activeOpacity={0.7}
                                            style={[
                                                styles.pillSmall,
                                                interetsTags.includes(label) &&
                                                    styles.pillSmallActive,
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    styles.pillSmallLabel,
                                                    interetsTags.includes(label) &&
                                                        styles.pillSmallLabelActive,
                                                ]}
                                            >
                                                {label}
                                            </Text>
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            </View>
                        ) : null}

                        <TextInput
                            value={interetsFreeText}
                            onChangeText={setInteretsFreeText}
                            style={styles.input}
                            placeholder="Autres : ex. astronomie, K-pop, échecs…"
                            placeholderTextColor={theme.colors.light}
                        />
                    </View>
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Voir ma reco perso"
                        onPress={onContinue}
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
    },
    content: {
        paddingTop: 14,
        paddingBottom: 24,
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        marginBottom: 4,
    },
    reassureRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginBottom: 16,
    },
    reassureText: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.light,
        lineHeight: 15,
    },
    block: {
        marginTop: 18,
    },
    blockTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    blockTitleRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    infoButton: {
        padding: 4,
        marginBottom: 8,
    },
    infoBox: {
        backgroundColor: theme.colors.lighter,
        borderRadius: 8,
        padding: 10,
        marginBottom: 10,
    },
    blockHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.light,
        marginTop: -4,
        marginBottom: 10,
    },
    optional: {
        fontFamily: "Montserrat-Regular",
        color: theme.colors.light,
    },
    rowCards: {
        flexDirection: "row",
        gap: 8,
    },
    rowCardWrap: {
        flex: 1,
    },
    sliderRow: {
        paddingVertical: 4,
    },
    slider: {
        width: "100%",
        height: 32,
    },
    sliderEnds: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 2,
    },
    sliderEnd: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.light,
    },
    sliderValue: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    pillsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    pill: {
        paddingHorizontal: 18,
        paddingVertical: 10,
        borderRadius: 22,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    pillActive: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primary,
    },
    pillLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
    },
    pillLabelActive: {
        color: theme.colors.white,
    },
    pillSmall: {
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    pillSmallActive: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    pillSmallLabel: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.grey,
    },
    pillSmallLabelActive: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
    },
    categoryPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 18,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    categoryPillActive: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    categoryEmoji: {
        fontSize: 14,
    },
    categoryLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.grey,
    },
    categoryLabelActive: {
        color: theme.colors.primary,
    },
    categoryBadge: {
        backgroundColor: theme.colors.primary,
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 10,
        minWidth: 16,
        alignItems: "center",
        marginLeft: 2,
    },
    categoryBadgeText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 10,
        color: theme.colors.white,
    },
    subChipsBox: {
        marginTop: 10,
        padding: 10,
        borderRadius: 10,
        backgroundColor: theme.colors.lighter,
    },
    input: {
        backgroundColor: theme.colors.lighter,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 11,
        fontSize: 13,
        fontFamily: "Montserrat-Regular",
        color: theme.colors.grey,
        marginTop: 10,
    },
    errorText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.error,
        marginTop: 6,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

const infoStyles = StyleSheet.create({
    row: {
        flexDirection: "row",
        gap: 8,
        marginBottom: 6,
    },
    label: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.primary,
        width: 40,
    },
    text: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        lineHeight: 15,
    },
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(null, mapDispatchToProps)(BrevetDiagnosticStep2Screen)
